import { Fragment, type MouseEvent } from 'react';

import { COLOR_TOKENS, type ColorToken } from '@backrs_ui/common-ui/src/designSystem/colors/colors';

import { Box, type SxProps, type Theme, Tooltip, Typography } from '@mui/material';

const SCALE: readonly ColorToken[] = [
  'Brand/50',
  'Brand/100',
  'Brand/200',
  'Brand/400',
  'Brand/600',
];

const LIGHT_TEXT_FROM = 3;

export type HeatGridDensity = 'compact' | 'comfortable';

const DENSITY = {
  compact: { gap: 'spacing-sm', highlightWidth: '1.5px' },
  comfortable: { gap: 'spacing-lg', highlightWidth: '1px' },
} as const;

export type HeatColumn = Readonly<{ id: string; label: string }>;

export type HeatRow = Readonly<{
  id: string;
  label: string;
  counts: Readonly<Record<string, number>>;
}>;

export type HeatGridProps = Readonly<{
  rows: readonly HeatRow[];
  columns: readonly HeatColumn[];
  xAxisLabel: string;
  yAxisLabel: string;
  /** Predicate for which cells to outline. */
  isHighlighted?: (rowId: string, columnId: string) => boolean;
  /** Hover text for a cell; omit for no tooltip. */
  cellTooltip?: (rowId: string, columnId: string, value: number) => string;
  /** Cells with a value become clickable when given. */
  onCellClick?: (rowId: string, columnId: string) => void;
  /** Prefix for each cell's element id, suffixed with `-{rowId}-{columnId}`. */
  id?: string;
  density?: HeatGridDensity;
}>;

/** Counts on a two-axis grid, shaded by how large each cell is. */
export const HeatGrid = ({
  rows,
  columns,
  xAxisLabel,
  yAxisLabel,
  isHighlighted,
  cellTooltip,
  onCellClick,
  id,
  density = 'compact',
}: HeatGridProps) => {
  const { gap, highlightWidth } = DENSITY[density];
  const max = Math.max(
    ...rows.flatMap((row) => columns.map((column) => row.counts[column.id] ?? 0)),
    0,
  );

  const scaleIndex = (value: number) => {
    if (value <= 0 || max <= 0) return 0;

    return Math.min(SCALE.length - 1, Math.ceil((value / max) * SCALE.length) - 1);
  };

  return (
    <Box sx={outerSx}>
      <Typography variant="text-xs-medium" sx={yAxisSx}>
        {yAxisLabel}
      </Typography>
      <Box sx={rightSx(gap)}>
        <Box sx={gridSx(columns.length, gap)}>
          {rows.map((row) => (
            <Fragment key={row.id}>
              <Typography variant="text-xs-medium" sx={rowLabelSx}>
                {row.label}
              </Typography>
              {columns.map((column) => {
                const value = row.counts[column.id] ?? 0;
                const index = scaleIndex(value);

                const isClickable = Boolean(onCellClick) && value > 0;
                const cell = (
                  <Box
                    key={column.id}
                    id={id && `${id}-${row.id}-${column.id}`}
                    sx={cellSx(
                      Boolean(isHighlighted?.(row.id, column.id)) && highlightWidth,
                      isClickable,
                    )}
                    style={{ backgroundColor: COLOR_TOKENS[SCALE[index]] }}
                    onClick={
                      isClickable
                        ? (event: MouseEvent) => {
                            event.stopPropagation();
                            onCellClick?.(row.id, column.id);
                          }
                        : undefined
                    }
                  >
                    <Typography
                      variant="text-xs-medium"
                      sx={index >= LIGHT_TEXT_FROM ? cellTextLightSx : cellTextSx}
                    >
                      {value}
                    </Typography>
                  </Box>
                );

                return cellTooltip ? (
                  <Tooltip
                    key={column.id}
                    arrow
                    placement="top"
                    title={cellTooltip(row.id, column.id, value)}
                  >
                    {cell}
                  </Tooltip>
                ) : (
                  cell
                );
              })}
            </Fragment>
          ))}
          <Box />
          {columns.map((column) => (
            <Typography key={column.id} variant="text-xs-medium" sx={columnLabelSx}>
              {column.label}
            </Typography>
          ))}
        </Box>
        <Typography variant="text-xs-medium" sx={xAxisSx}>
          {xAxisLabel}
        </Typography>
      </Box>
    </Box>
  );
};

type Gap = (typeof DENSITY)[HeatGridDensity]['gap'];

const outerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'stretch',
  gap: backrs.spacing['spacing-sm'],
  width: '100%',
});

const yAxisSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
  writingMode: 'vertical-rl',
  transform: 'rotate(180deg)',
  textAlign: 'center',
  flexShrink: 0,
  marginRight: backrs.spacing['spacing-md'],
});

const rightSx =
  (gap: Gap): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: backrs.spacing[gap],
    flex: 1,
    minWidth: 0,
  });

const gridSx =
  (columnCount: number, gap: Gap): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'grid',
    gridTemplateColumns: `auto repeat(${columnCount}, minmax(0, 1fr))`,
    alignItems: 'center',
    columnGap: backrs.spacing[gap],
    rowGap: backrs.spacing[gap],
    width: '100%',
  });

const columnLabelSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
  textAlign: 'center',
});

const rowLabelSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
  paddingRight: backrs.spacing['spacing-md'],
  whiteSpace: 'nowrap',
});

const xAxisSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
  textAlign: 'center',
  width: '100%',
  marginTop: backrs.spacing['spacing-md'],
});

const cellSx =
  (highlightWidth: string | false, isClickable: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    cursor: isClickable ? 'pointer' : 'default',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 0,
    minHeight: 28,
    // Narrower than this, a ratio plus the minimum height floors the cell at 50px and overflows.
    '@container (min-width: 650px)': {
      aspectRatio: '16 / 9',
    },
    borderRadius: backrs.radius['radius-sm'],
    border: highlightWidth
      ? `${highlightWidth} solid ${backrs.colors['Colors/Base/Black']}`
      : `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  });

const cellTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const cellTextLightSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Base/White'],
});
